import { useState, type FormEvent } from 'react';

const FORM_ID = 'dah9nrroh653ivfvnpa0';
const SUBMIT_ADDR = 'https://readdy.ai/api/form/dah9nrroh653ivfvnpa0';

export default function SubscribeForm() {
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setFormError('');
      return;
    }

    formData.delete('website_alt');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      payload.append(key, String(value));
    });

    setStatus('sending');
    setFormError('');

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');
      const isOk = response.ok && parsed?.code === 'OK' && !isSpam;

      if (isOk) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(isSpam ? '提交未通过校验，请稍后再试。' : serverMsg || '提交失败，请稍后再试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，请检查连接后重试。');
    }
  };

  return (
    <form
      id={FORM_ID}
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg bg-foreground-950 text-background-100 p-6 md:p-10"
    >
      <div className="max-w-xl">
        <span className="inline-flex items-center gap-2 text-xs font-label tracking-[0.28em] uppercase text-accent-400">
          <span className="w-6 h-px bg-accent-500" />
          每周一封整理信
        </span>
        <h3 className="mt-3 font-heading text-xl md:text-2xl font-bold text-background-50">
          订阅线报 · 别错过下一件内幕
        </h3>
        <p className="mt-3 text-sm text-background-200 leading-relaxed">
          包含本周新增深挖选题、三个最值得看的图解，以及正在跟进但尚未成稿的线索方向。
        </p>
      </div>

      <div className="mt-6 flex flex-col sm:flex-row gap-3">
        <div className="flex-1">
          <label htmlFor="subscribe-email" className="sr-only">邮箱地址</label>
          <input
            id="subscribe-email"
            type="email"
            name="email"
            required
            autoComplete="email"
            placeholder="请输入你的邮箱地址"
            className="w-full h-11 px-4 rounded-md bg-secondary-900 border border-secondary-700 text-sm text-background-50 placeholder:text-background-300 outline-none focus:border-accent-500 focus:ring-1 focus:ring-accent-500"
          />
        </div>
        <button
          type="submit"
          disabled={status === 'sending'}
          className="h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {status === 'sending' ? '提交中…' : '立即订阅'}
        </button>
      </div>

      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="alt-field-hide"
      />

      <div aria-live="polite" className="mt-3 min-h-[20px] text-xs">
        {status === 'success' && <span className="text-accent-400">订阅成功，感谢关注，下一封整理信会在周一寄出。</span>}
        {status === 'error' && <span className="text-primary-300">{formError}</span>}
      </div>
    </form>
  );
}